import {
  Checkbox,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { BanIcon, EyeIcon } from "lucide-react";
import { useRef } from "react";
import { RowActions } from "@/shared/ui/RowActions";
import type { CreditInvoiceListItemDTO } from "../api/client";
import {
  CreditInvoiceAmountCell,
  CreditInvoiceBookingCell,
  CreditInvoiceContactCell,
  CreditInvoiceContextCell,
  CreditInvoiceIssuedAtCell,
  CreditInvoiceStatusCell,
} from "./credit-invoice-table-cells";

type CreditInvoicesTableProps = {
  isLoading: boolean;
  data: CreditInvoiceListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (id: string) => void;
  canVoid?: boolean;
  onVoid?: (id: string) => void;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
};

export function CreditInvoicesTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
  onView,
  canVoid,
  onVoid,
  selectedIds = [],
  onSelectionChange,
}: CreditInvoicesTableProps) {
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const setSelection = (ids: string[]) => {
    onSelectionChangeRef.current?.(ids);
  };

  const columns: TanstackReactTable.ColumnDef<CreditInvoiceListItemDTO>[] = [
    ...(canVoid
      ? [
          {
            enableSorting: false,
            id: "invoiceSelect",
            header: "ເລືອກ",
            size: 72,
            cell: ({
              row,
            }: {
              row: { original: CreditInvoiceListItemDTO };
            }) => {
              const cancelled = row.original.status === "cancelled";
              const id = row.original.id;
              return (
                <Checkbox
                  disabled={cancelled}
                  checked={selectedIds.includes(id)}
                  onClick={(event) => event.stopPropagation()}
                  onCheckedChange={(checked) => {
                    if (cancelled) return;
                    const current = selectedIdsRef.current;
                    if (checked === true) {
                      if (current.includes(id)) return;
                      setSelection([...current, id]);
                      return;
                    }
                    setSelection(current.filter((selectedId) => selectedId !== id));
                  }}
                  aria-label="Select row"
                />
              );
            },
          } as TanstackReactTable.ColumnDef<CreditInvoiceListItemDTO>,
        ]
      : []),
    createSortableColumn<CreditInvoiceListItemDTO>("issuedAt", "ວັນທີອອກ", {
      size: 108,
      cell: ({ row }) => <CreditInvoiceIssuedAtCell row={row.original} />,
    }),
    {
      id: "contact",
      header: "ຜູ້ຕິດຕໍ່ / ນັກຮຽນ",
      size: 200,
      cell: ({ row }) => <CreditInvoiceContactCell row={row.original} />,
    },
    {
      id: "context",
      header: "ປະເພດ / ຫ້ອງ·ແພັກ",
      size: 188,
      cell: ({ row }) => <CreditInvoiceContextCell row={row.original} />,
    },
    {
      id: "bookingDate",
      header: "ວັນຈອງ",
      size: 128,
      cell: ({ row }) => <CreditInvoiceBookingCell row={row.original} />,
    },
    createSortableColumn<CreditInvoiceListItemDTO>("totalAmount", "ລວມ", {
      size: 112,
      cell: ({ row }) => (
        <CreditInvoiceAmountCell
          amount={row.original.totalAmount}
          tone="total"
          cancelled={row.original.status === "cancelled"}
        />
      ),
    }),
    createSortableColumn<CreditInvoiceListItemDTO>("paidAmount", "ຈ່າຍແລ້ວ", {
      size: 112,
      cell: ({ row }) => (
        <CreditInvoiceAmountCell
          amount={row.original.paidAmount}
          tone="paid"
          cancelled={row.original.status === "cancelled"}
        />
      ),
    }),
    createSortableColumn<CreditInvoiceListItemDTO>("remainingAmount", "ຄ້າງ", {
      size: 112,
      cell: ({ row }) => (
        <CreditInvoiceAmountCell
          amount={row.original.remainingAmount}
          tone="remaining"
          cancelled={row.original.status === "cancelled"}
        />
      ),
    }),
    createSortableColumn<CreditInvoiceListItemDTO>("status", "ສະຖານະ", {
      size: 132,
      cell: ({ row }) => <CreditInvoiceStatusCell row={row.original} />,
    }),
    {
      id: "actions",
      size: 220,
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="size-4" />,
              onClick: () => onView(row.original.id),
            },
            ...(canVoid &&
            onVoid &&
            row.original.status !== "cancelled" &&
            row.original.refType !== "partner_project_sale"
              ? [
                  {
                    label: "ຍົກເລີກ",
                    icon: <BanIcon className="size-4" />,
                    onClick: () => onVoid(row.original.id),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<CreditInvoiceListItemDTO, unknown>
      noDataMessage="ບໍ່ພົບລາຍການ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting || sorting.length === 0) return;
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
      }}
    />
  );
}
